import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScanLine, Loader2 } from "lucide-react";
import { API } from "@/apiConfig";
import { getScanSettings } from "@/lib/scanSettings";
import CompactScannerPanel from "@/components/CompactScannerPanel";

/**
 * ScanInvoiceButton — Scan button with optional inline scanner controls.
 * When `sharedSettingsRef` is provided, only renders a compact Scan button (no panel).
 * When not provided, renders the full inline scanner panel + button (legacy mode).
 */
export default function ScanInvoiceButton({ vendorName, vendorId, amount, category = "Invoice", payoutId, onDocCreated, disabled, className = "", size = "sm", sharedSettingsRef }) {
  const [scanning, setScanning] = useState(false);
  const localSettingsRef = useRef(null);
  const activeRef = sharedSettingsRef || localSettingsRef;

  const isElectron = !!window.electronAPI?.scanDocument;

  const handleScan = async () => {
    if (!isElectron) {
      toast.error("Scanner only available in desktop app");
      return;
    }

    const settings = activeRef.current;
    if (!settings?.scannerId) {
      toast.error("No scanner selected. Pick a scanner from the dropdown.");
      return;
    }

    setScanning(true);
    try {
      const result = await window.electronAPI.scanDocument(settings.scannerId, settings);
      if (result.error) {
        toast.error(result.message || "Scan failed");
        return;
      }
      if (!result.data) {
        toast.error("Scanner returned no data");
        return;
      }

      // Convert base64 to File — use fetch() for non-blocking conversion
      const dataUrl = `data:application/pdf;base64,${result.data}`;
      const blobRes = await fetch(dataUrl);
      const blob = await blobRes.blob();
      const todayStr = new Date().toLocaleDateString("en-CA");
      const fileName = `${(vendorName || "Scan").toUpperCase()} - ${todayStr}.pdf`;
      const file = new File([blob], fileName, { type: "application/pdf" });

      // Find the matching category ID
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      let categoryId = null;
      try {
        const catRes = await axios.get(`${API}/document_categories`, { headers });
        const cats = catRes.data || [];
        const match = cats.find(c => c.name.toLowerCase() === category.toLowerCase() && !c.parent_id);
        if (match) {
          categoryId = match.id;
        } else {
          const newCat = await axios.post(`${API}/document_categories`, { name: category }, { headers });
          categoryId = newCat.data?.id || null;
        }
      } catch {}

      // Create document via API
      const fd = new FormData();
      fd.append("name", `${(vendorName || "SCAN").toUpperCase()} - ${todayStr}`);
      fd.append("date", todayStr);
      if (vendorId) fd.append("vendor_id", vendorId);
      if (vendorName) fd.append("vendor_name", vendorName);
      if (categoryId) fd.append("category_id", categoryId);
      fd.append("notes", "");
      if (amount) fd.append("amount", parseFloat(amount) || 0);
      fd.append("paid", "true");
      fd.append("files", file);

      const docRes = await axios.post(`${API}/documents`, fd, { headers });
      const doc = docRes.data;

      // Link document to payout
      if (payoutId && doc?.id) {
        try {
          await axios.patch(`${API}/vendor-payouts/${payoutId}/link-document`, { document_id: doc.id }, { headers });
        } catch {}
      }

      let msg = `Invoice scanned! ${result.pageCount || 1} page(s)`;
      if (result.blanksRemoved > 0) msg += `, ${result.blanksRemoved} blank(s) skipped`;
      toast.success(msg);

      onDocCreated && onDocCreated(doc);
    } catch (err) {
      toast.error(`Scan failed: ${err.message || "Unknown error"}`);
    } finally {
      setScanning(false);
      // Restore focus after scan — Electron IPC can steal focus from Radix Dialog
      requestAnimationFrame(() => {
        // Focus the nearest active dialog or the active element's closest input
        const activeDialog = document.querySelector('[role="dialog"]:not([hidden])');
        if (activeDialog) {
          const btn = activeDialog.querySelector('[data-testid="scan-invoice-btn"], [data-testid="vendor-payout-done"], button:not([disabled])');
          if (btn) btn.focus();
        }
      });
    }
  };

  if (!isElectron) return null;

  const isXs = size === "xs";

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} data-testid="scan-invoice-section">
      {!sharedSettingsRef && <CompactScannerPanel onSettingsReady={(s) => { localSettingsRef.current = s; }} />}
      <Button
        variant="outline"
        size="sm"
        className={`border-blue-300 text-blue-700 hover:bg-blue-50 ${isXs ? "h-6 text-[10px] px-1.5" : ""}`}
        onClick={handleScan}
        disabled={disabled || scanning}
        data-testid="scan-invoice-btn"
      >
        {scanning ? <Loader2 className={`${isXs ? "w-3 h-3" : "w-3.5 h-3.5"} mr-1 animate-spin`} /> : <ScanLine className={`${isXs ? "w-3 h-3" : "w-3.5 h-3.5"} mr-1`} />}
        {scanning ? "Scanning..." : "Scan"}
      </Button>
    </div>
  );
}
